import { useCallback, useMemo } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { makeAccessorFunction } from '../../hooks/makeAccessorFunction.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type { DataTableSubRowsProps } from './sub-rows-types.js';

/**
 * Default accessor for the subRows
 * @internal
 */
export function defaultSubRowsAccessor<TData>(originalRow: TData): TData[] {
  return (originalRow as TData & { subRows: TData[] })?.subRows;
}

const defaultState = {};

/**
 * Configuration hook for subrows of the DataTable.
 * @internal
 */
export function useSubRows<TData extends DataTableRowData>(
  props: DataTableSubRowsProps<TData>,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const { subRows } = props;

  const [openSubRows, setOpenSubRows] = useControllableState({
    value: props.openSubRows,
    defaultValue: props.defaultOpenSubRows,
    onChange: (value) => {
      if (value !== true) {
        props.onOpenSubRowsChange?.(value);
      }
    },
  });

  const subRowsAccessorFn = useCallback(
    (originalRow: TData) => {
      if (
        subRows !== undefined &&
        typeof subRows !== 'boolean' &&
        typeof subRows.accessor === 'function'
      ) {
        return subRows.accessor(originalRow) as Exclude<TData, unknown>[];
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps, typescript/no-explicit-any
    [!!subRows, (subRows as any)?.accessor],
  );

  options.getSubRows = useMemo(() => {
    // If `subRows` is only set as a boolean prop, we can default to the standard
    // subrow accessor.
    if (typeof subRows === 'boolean') {
      return defaultSubRowsAccessor;
    }
    if (typeof subRows === 'object') {
      // If a config object is given, we can handle defaulting here as well
      const { accessor } = subRows;

      // Default the accessor if subRows are enabled, but no accessor is given.
      if (accessor === undefined) {
        return defaultSubRowsAccessor;
      }
      // Allow a simple string accessor for the sub rows.
      else if (typeof accessor === 'string') {
        if (accessor.includes('.') || accessor.includes('[')) {
          const pathAccessFunction = makeAccessorFunction(accessor);
          return (originalRow: TData) =>
            pathAccessFunction(originalRow) as Exclude<TData, unknown>[];
        } else {
          return (originalRow: TData) =>
            originalRow[accessor as keyof TData] as Exclude<TData, unknown>[];
        }
      }
      // If a function is given, we can use this one to get access to the sub rows.
      else if (typeof accessor === 'function') {
        return subRowsAccessorFn;
      }
    }
    return undefined;
    // Requires a bit of a complex dependency array to avoid unnecessary re-creation of the accessor function, while still updating if the accessor configuration changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps, typescript/no-explicit-any
  }, [!!subRows, (subRows as any)?.accessor, subRowsAccessorFn]);

  if (subRows === undefined || subRows === false) {
    options.enableExpanding = false;
    return;
  }

  options.enableExpanding = true;

  // If `subRows` is only set as a boolean prop, we can default to the standard
  // subrow accessor.
  if (typeof subRows !== 'boolean') {
    // If a config object is given, we can handle defaulting here as well
    const { subRowColumnId: subrowColumnId, disableSubRow } = subRows;

    // If a custom columnId is given for the injection,
    // we map that into the options to be respected in `getSubRowIndicatorColumnId`
    if (subrowColumnId) {
      options.subRowColumnId = subrowColumnId;
    }

    // If a custom disable configuration is given, we can map that to the
    // getRowCanExpand option and shimming the internal tanstack `Row` object.
    if (disableSubRow) {
      // Cast is required to align the DataTableRowData generic and keep the excluded unknown for the public API
      // while still allowing internal access to the full row data for the function.
      options.getRowExpandDisabled = disableSubRow as (
        row: DataTableRowData,
      ) => boolean;
    }
  }

  // Pass the state through to the table.
  options.state = {
    ...options.state,
    expanded: openSubRows ?? defaultState,
  };
  options.onExpandedChange = setOpenSubRows;
}
